/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useMemo } from 'react'

import {
  EuiModal,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiModalBody,
  EuiModalFooter,
  EuiButton,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { TrafficFilterRulesetInfo } from '@modules/cloud-api/v1/types'
import type { TrafficFilterInfo } from '@modules/network-user-api/v1/types'

import ProtectedResourcesList from './List'

type Props = {
  rulesets: {
    hosted?: TrafficFilterRulesetInfo[]
    serverless?: TrafficFilterInfo[]
  }
  onClose: () => void
}

const ProtectedResourcesModal: React.FunctionComponent<Props> = ({ rulesets, onClose }) => {
  const modalTitle = useMemo(() => {
    if (rulesets.hosted?.length && !rulesets.serverless?.length) {
      return (
        <FormattedMessage
          id='networkSecurity.protectedDeployments.title'
          defaultMessage='Protected deployments'
        />
      )
    }

    if (rulesets.serverless?.length && !rulesets.hosted?.length) {
      return (
        <FormattedMessage
          id='networkSecurity.protectedProjects.title'
          defaultMessage='Protected projects'
        />
      )
    }

    return (
      <FormattedMessage
        id='networkSecurity.protectedResources.title'
        defaultMessage='Protected resources'
      />
    )
  }, [rulesets])

  return (
    <EuiModal onClose={onClose}>
      <div style={{ width: '40rem' }}>
        <EuiModalHeader>
          <EuiModalHeaderTitle>{modalTitle}</EuiModalHeaderTitle>
        </EuiModalHeader>
        <EuiModalBody>
          <ProtectedResourcesList rulesets={rulesets} />
        </EuiModalBody>
        <EuiModalFooter>
          <EuiButton fill={true} onClick={onClose}>
            <FormattedMessage
              id='networkSecurity.protectedResources.modal.close'
              defaultMessage='Close'
            />
          </EuiButton>
        </EuiModalFooter>
      </div>
    </EuiModal>
  )
}

export default ProtectedResourcesModal
